import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';
import DetailModalShell from '../components/detail/DetailModalShell';
import JsonTreeViewer from '../components/detail/JsonTreeViewer';

const debounceDelay = 300;
const defaultRowsPerPage = 25;

const OPERATION_COLUMNS = [
  { key: 'select', label: '', minWidth: 56, maxWidth: 90, defaultWidth: 56, resizable: false },
  { key: 'status', label: 'Status', minWidth: 130, maxWidth: 240, defaultWidth: 150 },
  { key: 'provider', label: 'Provider', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'operation', label: 'Operation', minWidth: 220, maxWidth: 520, defaultWidth: 280 },
  { key: 'resource', label: 'Resource', minWidth: 240, maxWidth: 560, defaultWidth: 320 },
  { key: 'field_group', label: 'Field', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'attempts', label: 'Attempts', minWidth: 100, maxWidth: 160, defaultWidth: 110 },
  { key: 'next_attempt', label: 'Next Attempt', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'error', label: 'Error', minWidth: 260, maxWidth: 760, defaultWidth: 360 },
];

const SYNC_COLUMNS = [
  { key: 'status', label: 'Status', minWidth: 130, maxWidth: 240, defaultWidth: 150 },
  { key: 'provider', label: 'Provider', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'resource', label: 'Resource', minWidth: 260, maxWidth: 580, defaultWidth: 340 },
  { key: 'field_group', label: 'Field', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'verified', label: 'Verified', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'error', label: 'Error', minWidth: 260, maxWidth: 760, defaultWidth: 360 },
];

const PRICING_COLUMNS = [
  { key: 'select', label: '', minWidth: 56, maxWidth: 90, defaultWidth: 56, resizable: false },
  { key: 'status', label: 'Status', minWidth: 130, maxWidth: 240, defaultWidth: 150 },
  { key: 'blueprint', label: 'Blueprint', minWidth: 260, maxWidth: 620, defaultWidth: 340 },
  { key: 'product_type', label: 'Product Type', minWidth: 170, maxWidth: 360, defaultWidth: 210 },
  { key: 'target_shop', label: 'Target Shop', minWidth: 170, maxWidth: 340, defaultWidth: 210 },
  { key: 'target_section', label: 'Target Section', minWidth: 170, maxWidth: 340, defaultWidth: 210 },
  { key: 'target_price', label: 'Target Price', minWidth: 130, maxWidth: 220, defaultWidth: 150 },
  { key: 'progress', label: 'Channel Progress', minWidth: 190, maxWidth: 320, defaultWidth: 230 },
  { key: 'attempts', label: 'Attempts', minWidth: 100, maxWidth: 160, defaultWidth: 110 },
  { key: 'next_attempt', label: 'Next Attempt', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'synced', label: 'Completed', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
  { key: 'error', label: 'Error', minWidth: 260, maxWidth: 760, defaultWidth: 360 },
];

const providerOptions = [
  { value: '', label: 'All providers' },
  { value: 'etsy', label: 'Etsy' },
  { value: 'printify', label: 'Printify' },
  { value: 'woocommerce', label: 'WooCommerce' },
];

const operationStatusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'failed', label: 'Failed' },
  { value: 'blocked', label: 'Blocked' },
];

const syncStatusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'dirty', label: 'Dirty' },
  { value: 'queued', label: 'Queued' },
  { value: 'in_sync', label: 'In sync' },
  { value: 'failed', label: 'Failed' },
];

const pricingStatusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'partial', label: 'Needs attention' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'failed', label: 'Failed' },
];

const resourceTypeOptions = [
  { value: '', label: 'All resources' },
  { value: 'etsy_listing', label: 'Etsy listings' },
  { value: 'printify_product', label: 'Printify products' },
  { value: 'woocommerce_listing', label: 'WooCommerce listings' },
];

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString();
}

function formatMoney(cents, currency = 'USD') {
  if (typeof cents !== 'number' || Number.isNaN(cents) || cents <= 0) {
    return '-';
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(cents / 100);
  } catch (err) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function labelize(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }
  return raw
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function statusColor(status) {
  switch (String(status || '').toLowerCase()) {
    case 'succeeded':
    case 'in_sync':
      return 'success';
    case 'failed':
    case 'blocked':
      return 'error';
    case 'partial':
      return 'warning';
    case 'running':
    case 'queued':
      return 'primary';
    case 'pending':
    case 'dirty':
      return 'warning';
    default:
      return 'default';
  }
}

function canRetry(operation) {
  return ['failed', 'blocked', 'pending'].includes(String(operation?.status || '').toLowerCase());
}

function canRetryPricing(sync) {
  return ['failed', 'partial'].includes(String(sync?.status || '').toLowerCase());
}

function getPricingBlueprint(sync) {
  return sync?.blueprint || {};
}

function getPricingTargetPrice(sync) {
  const blueprint = getPricingBlueprint(sync);
  return formatMoney(blueprint.target_price, blueprint.target_price_currency);
}

function ResourceLabel({ item }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap title={item.resource_id}>
        {labelize(item.resource_type)}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap title={item.resource_id} sx={{ display: 'block' }}>
        {item.resource_id || '-'}
      </Typography>
    </Box>
  );
}

function DetailJson({ title, data }) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      <Paper variant="outlined" sx={{ p: 1, maxHeight: 360, overflowY: 'auto' }}>
        <JsonTreeViewer data={data ?? {}} />
      </Paper>
    </Box>
  );
}

const OperationsPage = () => {
  const { fetchWithAuth } = useAuth();
  const [tab, setTab] = React.useState('operations');
  const [operations, setOperations] = React.useState([]);
  const [syncStates, setSyncStates] = React.useState([]);
  const [pricingSyncs, setPricingSyncs] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [provider, setProvider] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [resourceType, setResourceType] = React.useState('');
  const [fieldGroup, setFieldGroup] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [retrying, setRetrying] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [message, setMessage] = React.useState(null);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [detailItem, setDetailItem] = React.useState(null);
  const operationColumns = useResizableColumns(OPERATION_COLUMNS, { storageKey: 'table.external-operations' });
  const syncColumns = useResizableColumns(SYNC_COLUMNS, { storageKey: 'table.resource-sync-states' });
  const pricingColumns = useResizableColumns(PRICING_COLUMNS, { storageKey: 'table.printify-pricing-syncs' });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  React.useEffect(() => {
    setStatus('');
    if (tab === 'pricing') {
      setProvider('');
      setResourceType('');
      setFieldGroup('');
    }
  }, [tab]);

  React.useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
  }, [debouncedSearch, fieldGroup, provider, resourceType, rowsPerPage, status, tab]);

  const loadItems = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      params.set('sort', 'desc');
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (tab !== 'pricing' && provider) {
        params.set('provider', provider);
      }
      if (status) {
        params.set('status', status);
      }
      if (tab !== 'pricing' && resourceType) {
        params.set('resource_type', resourceType);
      }
      if (tab !== 'pricing' && fieldGroup) {
        params.set('field_group', fieldGroup);
      }
      const endpoint = tab === 'sync'
        ? '/resource-sync-states'
        : tab === 'pricing'
          ? '/printify/blueprint-pricing-syncs'
          : '/external-operations';
      const response = await fetchWithAuth(`${config.apiBaseUrl}${endpoint}?${params.toString()}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load operations');
      }
      const items = Array.isArray(payload.items) ? payload.items : [];
      if (tab === 'sync') {
        setSyncStates(items);
        setOperations([]);
        setPricingSyncs([]);
      } else if (tab === 'pricing') {
        setPricingSyncs(items);
        setOperations([]);
        setSyncStates([]);
      } else {
        setOperations(items);
        setSyncStates([]);
        setPricingSyncs([]);
      }
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to load operations');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fetchWithAuth, fieldGroup, page, provider, resourceType, rowsPerPage, status, tab]);

  React.useEffect(() => {
    loadItems();
  }, [loadItems]);

  const visibleSelectableIds = React.useMemo(() => {
    if (tab === 'pricing') {
      return pricingSyncs.map((item) => item.printify_blueprint_id).filter(Boolean);
    }
    if (tab === 'operations') {
      return operations.map((item) => item.id).filter(Boolean);
    }
    return [];
  }, [operations, pricingSyncs, tab]);
  const retryableSelectedIds = React.useMemo(() => {
    if (tab === 'pricing') {
      return pricingSyncs
        .filter((item) => selectedIds.has(item.printify_blueprint_id) && canRetryPricing(item))
        .map((item) => item.printify_blueprint_id);
    }
    return operations.filter((item) => selectedIds.has(item.id) && canRetry(item)).map((item) => item.id);
  }, [operations, pricingSyncs, selectedIds, tab]);
  const selectedVisibleCount = visibleSelectableIds.filter((id) => selectedIds.has(id)).length;
  const allSelected = visibleSelectableIds.length > 0 && selectedVisibleCount === visibleSelectableIds.length;
  const indeterminate = selectedVisibleCount > 0 && selectedVisibleCount < visibleSelectableIds.length;

  const toggleAll = (event) => {
    const checked = event.target.checked;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      visibleSelectableIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const retryOperations = async (ids) => {
    const retryIds = Array.from(new Set(ids)).filter(Boolean);
    if (retryIds.length === 0) {
      return;
    }
    setRetrying(true);
    setError(null);
    setMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/external-operation-retries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: retryIds }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to retry operations');
      }
      const failures = Array.isArray(payload.failures) ? payload.failures : [];
      setMessage({
        severity: failures.length ? 'warning' : 'success',
        text: failures.length
          ? `Queued ${retryIds.length - failures.length} operation retries; ${failures.length} failed.`
          : `Queued ${retryIds.length} operation ${retryIds.length === 1 ? 'retry' : 'retries'}.`,
      });
      setSelectedIds(new Set());
      await loadItems();
    } catch (err) {
      setError(err.message || 'Failed to retry operations');
    } finally {
      setRetrying(false);
    }
  };

  const retryPricingSyncs = async (ids) => {
    const retryIds = Array.from(new Set(ids)).filter(Boolean);
    if (retryIds.length === 0) {
      return;
    }
    setRetrying(true);
    setError(null);
    setMessage(null);
    try {
      const response = await fetchWithAuth(`${config.apiBaseUrl}/printify/blueprint-pricing-syncs/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blueprint_ids: retryIds }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to retry channel pricing');
      }
      const failures = Array.isArray(payload.failures) ? payload.failures : [];
      const queued = typeof payload.queued === 'number' ? payload.queued : retryIds.length - failures.length;
      setMessage({
        severity: failures.length ? 'warning' : 'success',
        text: failures.length
          ? `Queued ${queued} channel pricing ${queued === 1 ? 'retry' : 'retries'}; ${failures.length} failed.`
          : `Queued ${queued} channel pricing ${queued === 1 ? 'retry' : 'retries'}.`,
      });
      setSelectedIds(new Set());
      await loadItems();
    } catch (err) {
      setError(err.message || 'Failed to retry channel pricing');
    } finally {
      setRetrying(false);
    }
  };

  const isSyncTab = tab === 'sync';
  const isPricingTab = tab === 'pricing';
  const rows = isSyncTab ? syncStates : isPricingTab ? pricingSyncs : operations;
  const columns = isSyncTab ? syncColumns : isPricingTab ? pricingColumns : operationColumns;
  const columnDefs = isSyncTab ? SYNC_COLUMNS : isPricingTab ? PRICING_COLUMNS : OPERATION_COLUMNS;
  const statusOptions = isSyncTab ? syncStatusOptions : isPricingTab ? pricingStatusOptions : operationStatusOptions;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
        <TextField
          fullWidth
          placeholder={isPricingTab ? 'Search channel pricing by blueprint, shop, section, ID, or error' : 'Search operations by ID, resource, type, or error'}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        {!isPricingTab ? (
          <TextField select label="Provider" value={provider} onChange={(event) => setProvider(event.target.value)} sx={{ minWidth: 160 }}>
            {providerOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField select label="Status" value={status} onChange={(event) => setStatus(event.target.value)} sx={{ minWidth: 170 }}>
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        {!isPricingTab ? (
          <TextField
            select
            label="Resource"
            value={resourceType}
            onChange={(event) => setResourceType(event.target.value)}
            sx={{ minWidth: 190 }}
          >
            {resourceTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        ) : null}
        {!isPricingTab ? (
          <TextField
            select
            label="Field"
            value={fieldGroup}
            onChange={(event) => setFieldGroup(event.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All fields</MenuItem>
            <MenuItem value="content">Content</MenuItem>
            <MenuItem value="section">Section</MenuItem>
            <MenuItem value="pricing">Pricing</MenuItem>
            <MenuItem value="publish">Publish</MenuItem>
          </TextField>
        ) : null}
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress color="inherit" size={16} /> : <RefreshIcon />}
          onClick={loadItems}
          disabled={loading}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Refresh
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}

      <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Tabs value={tab} onChange={(event, next) => setTab(next)} aria-label="Operations dashboard tabs">
            <Tab value="operations" label="Operations" />
            <Tab value="sync" label="Sync State" />
            <Tab value="pricing" label="Channel Pricing" />
          </Tabs>
          {tab === 'operations' || tab === 'pricing' ? (
            <Button
              variant="contained"
              startIcon={retrying ? <CircularProgress color="inherit" size={16} /> : <ReplayIcon />}
              onClick={() => (isPricingTab ? retryPricingSyncs(retryableSelectedIds) : retryOperations(retryableSelectedIds))}
              disabled={retrying || retryableSelectedIds.length === 0}
            >
              Retry Selected
            </Button>
          ) : null}
        </Box>
        <Divider />
        <TableContainer sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: columns.tableMinWidth }}>
            <ResizableColGroup columns={columnDefs} getWidth={columns.getWidth} />
            <TableHead>
              <TableRow>
                {columnDefs.map((column) => {
                  if (column.key === 'select') {
                    return (
                      <TableCell key={column.key} padding="checkbox" sx={{ width: columns.getWidth(column.key) }}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={indeterminate}
                          onChange={toggleAll}
                          disabled={visibleSelectableIds.length === 0}
                        />
                      </TableCell>
                    );
                  }
                  return (
                    <ResizableHeaderCell
                      key={column.key}
                      column={column}
                      getWidth={columns.getWidth}
                      onResizeStart={columns.startColumnResize}
                    />
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={columnDefs.length} />
              ) : rows.length === 0 ? (
                <TableEmptyRow colSpan={columnDefs.length} label="No operations found" />
              ) : isSyncTab ? (
                syncStates.map((item) => (
                  <TableRow key={item.id} hover onClick={() => setDetailItem({ type: 'sync', item })} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Chip size="small" color={statusColor(item.status)} label={labelize(item.status)} />
                    </TableCell>
                    <TableCell>{labelize(item.provider)}</TableCell>
                    <TableCell><ResourceLabel item={item} /></TableCell>
                    <TableCell>{labelize(item.field_group)}</TableCell>
                    <TableCell>{formatDate(item.last_verified_at)}</TableCell>
                    <TableCell>{formatDate(item.updated_at)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={item.last_error ? 'error.main' : 'text.secondary'} noWrap title={item.last_error}>
                        {item.last_error || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              ) : isPricingTab ? (
                pricingSyncs.map((item) => {
                  const blueprint = getPricingBlueprint(item);
                  return (
                    <TableRow key={item.id} hover onClick={() => setDetailItem({ type: 'pricing', item })} sx={{ cursor: 'pointer' }}>
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(item.printify_blueprint_id)}
                          onChange={() => toggleRow(item.printify_blueprint_id)}
                          disabled={!canRetryPricing(item)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={statusColor(item.status)} label={labelize(item.status)} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap title={blueprint.title}>
                            {blueprint.title || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap title={item.printify_blueprint_id} sx={{ display: 'block' }}>
                            {blueprint.external_id ? `Blueprint ${blueprint.external_id}` : item.printify_blueprint_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={blueprint.product_type}>
                          {blueprint.product_type || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={item.target_shop?.title}>
                          {item.target_shop?.title || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={item.target_section?.title}>
                          {item.target_section?.title || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getPricingTargetPrice(item)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          Etsy {item.etsy_succeeded ?? 0}/{item.expected_etsy_listings ?? 0}, Woo {item.woo_succeeded ?? 0}/{item.expected_woo_listings ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.attempts ?? 0}</TableCell>
                      <TableCell>{formatDate(item.next_attempt_at)}</TableCell>
                      <TableCell>{formatDate(item.last_synced_at)}</TableCell>
                      <TableCell>{formatDate(item.updated_at)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={item.last_error ? 'error.main' : 'text.secondary'} noWrap title={item.last_error}>
                          {item.last_error || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                operations.map((item) => (
                  <TableRow key={item.id} hover onClick={() => setDetailItem({ type: 'operation', item })} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleRow(item.id)}
                        disabled={!canRetry(item)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(item.status)} label={labelize(item.status)} />
                    </TableCell>
                    <TableCell>{labelize(item.provider)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap title={item.operation_type}>
                        {labelize(item.operation_type)}
                      </Typography>
                    </TableCell>
                    <TableCell><ResourceLabel item={item} /></TableCell>
                    <TableCell>{labelize(item.field_group)}</TableCell>
                    <TableCell>{item.attempts ?? 0}</TableCell>
                    <TableCell>{formatDate(item.next_attempt_at)}</TableCell>
                    <TableCell>{formatDate(item.updated_at)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={item.last_error ? 'error.main' : 'text.secondary'} noWrap title={item.last_error}>
                        {item.last_error || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <DetailModalShell
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        title={
          detailItem?.type === 'sync'
            ? 'Resource Sync State'
            : detailItem?.type === 'pricing'
              ? 'Channel Pricing Rollout'
              : 'External Operation'
        }
        externalId={detailItem?.item?.id}
        left={(
          <Stack spacing={2}>
            {detailItem?.type === 'pricing' ? (
              <>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip size="small" color={statusColor(detailItem?.item?.status)} label={labelize(detailItem?.item?.status)} />
                  <Chip size="small" label="Etsy + WooCommerce" />
                  <Chip size="small" label="Pricing" />
                </Stack>
                <Box>
                  <Typography variant="subtitle2">Blueprint</Typography>
                  <Typography variant="body2">{detailItem?.item?.blueprint?.title || '-'}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {detailItem?.item?.blueprint?.external_id
                      ? `Blueprint ${detailItem.item.blueprint.external_id}`
                      : detailItem?.item?.printify_blueprint_id || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Planning</Typography>
                  <Typography variant="body2">Product type: {detailItem?.item?.blueprint?.product_type || '-'}</Typography>
                  <Typography variant="body2">Target price: {getPricingTargetPrice(detailItem?.item)}</Typography>
                  <Typography variant="body2">Source shop: {detailItem?.item?.source_shop?.title || '-'}</Typography>
                  <Typography variant="body2">Target shop: {detailItem?.item?.target_shop?.title || '-'}</Typography>
                  <Typography variant="body2">Target section: {detailItem?.item?.target_section?.title || '-'}</Typography>
                  <Typography variant="body2">Revision: {detailItem?.item?.revision || '-'}</Typography>
                  <Typography variant="body2">
                    Progress: Etsy {detailItem?.item?.etsy_succeeded ?? 0}/{detailItem?.item?.expected_etsy_listings ?? 0}, Woo {detailItem?.item?.woo_succeeded ?? 0}/{detailItem?.item?.expected_woo_listings ?? 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Attempts</Typography>
                  <Typography variant="body2">{detailItem?.item?.attempts ?? 0}</Typography>
                </Box>
                <DetailJson title="State" data={detailItem?.item} />
              </>
            ) : (
              <>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip size="small" color={statusColor(detailItem?.item?.status)} label={labelize(detailItem?.item?.status)} />
                  <Chip size="small" label={labelize(detailItem?.item?.provider)} />
                  <Chip size="small" label={labelize(detailItem?.item?.field_group)} />
                </Stack>
                <Box>
                  <Typography variant="subtitle2">Resource</Typography>
                  <Typography variant="body2">{labelize(detailItem?.item?.resource_type)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {detailItem?.item?.resource_id || '-'}
                  </Typography>
                </Box>
                {detailItem?.type === 'operation' ? (
                  <>
                    <Box>
                      <Typography variant="subtitle2">Operation</Typography>
                      <Typography variant="body2">{labelize(detailItem?.item?.operation_type)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Attempts</Typography>
                      <Typography variant="body2">{detailItem?.item?.attempts ?? 0}</Typography>
                    </Box>
                    <DetailJson title="Payload" data={detailItem?.item?.payload} />
                    <DetailJson title="Last Response" data={detailItem?.item?.last_response} />
                  </>
                ) : (
                  <DetailJson title="State" data={detailItem?.item} />
                )}
              </>
            )}
          </Stack>
        )}
        right={(
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">Timing</Typography>
              <Typography variant="body2">Created: {formatDate(detailItem?.item?.created_at)}</Typography>
              <Typography variant="body2">Updated: {formatDate(detailItem?.item?.updated_at)}</Typography>
              {detailItem?.type === 'operation' ? (
                <>
                  <Typography variant="body2">Last attempt: {formatDate(detailItem?.item?.last_attempt_at)}</Typography>
                  <Typography variant="body2">Next attempt: {formatDate(detailItem?.item?.next_attempt_at)}</Typography>
                  <Typography variant="body2">Completed: {formatDate(detailItem?.item?.completed_at)}</Typography>
                </>
              ) : detailItem?.type === 'pricing' ? (
                <>
                  <Typography variant="body2">Next attempt: {formatDate(detailItem?.item?.next_attempt_at)}</Typography>
                  <Typography variant="body2">In progress: {formatDate(detailItem?.item?.in_progress_at)}</Typography>
                  <Typography variant="body2">Completed: {formatDate(detailItem?.item?.completed_at || detailItem?.item?.last_synced_at)}</Typography>
                </>
              ) : (
                <Typography variant="body2">Verified: {formatDate(detailItem?.item?.last_verified_at)}</Typography>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2">Error</Typography>
              <Typography variant="body2" color={detailItem?.item?.last_error ? 'error.main' : 'text.secondary'} sx={{ overflowWrap: 'anywhere' }}>
                {detailItem?.item?.last_error || '-'}
              </Typography>
            </Box>
            {detailItem?.type === 'sync' ? (
              <Box>
                <Typography variant="subtitle2">Hashes</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  Desired: {detailItem?.item?.desired_hash || '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                  Remote: {detailItem?.item?.remote_hash || '-'}
                </Typography>
              </Box>
            ) : null}
            {detailItem?.type === 'operation' && canRetry(detailItem?.item) ? (
              <Button
                variant="contained"
                startIcon={retrying ? <CircularProgress color="inherit" size={16} /> : <ReplayIcon />}
                onClick={() => retryOperations([detailItem.item.id])}
                disabled={retrying}
              >
                Retry Operation
              </Button>
            ) : null}
            {detailItem?.type === 'pricing' && canRetryPricing(detailItem?.item) ? (
              <Button
                variant="contained"
                startIcon={retrying ? <CircularProgress color="inherit" size={16} /> : <ReplayIcon />}
                onClick={() => retryPricingSyncs([detailItem.item.printify_blueprint_id])}
                disabled={retrying}
              >
                Retry Channel Pricing
              </Button>
            ) : null}
          </Stack>
        )}
      />
    </Box>
  );
};

export default OperationsPage;
